import { Component, Input, OnInit } from '@angular/core';
import { CplRequestItem } from 'src/app/model/cpl-request-item.model';
import { CplUserRequest } from 'src/app/model/cpl-user-request.model';
import { SessionService, PortalSettings } from '@edl-development/edl-common';
import { RequestUserStatus } from 'src/app/model/enum/request-user-status.enum';
import { TimeTrackingTypes } from 'src/app/model/enum/cpl-time-tracking-type.enum';
import { A48HelpersService } from 'src/app/services/a48-helpers/a48-helpers.service';
import { RequestHelpersService } from 'src/app/services/request-helpers/request-helpers.service';
import { CplRosterActionsTypes } from 'src/app/shared-compliance-items/enums/cpl-roster-actions-types.enum';
import { StateService } from '@uirouter/core';
import { RequestItemService } from 'src/app/services/request-item.service';
import { firstValueFrom } from 'rxjs';
import {UserRequestActionsTypes} from "../../../../shared-request-items/enum/user-request-actions-types.enum";
import { SubmissionsSortField } from '../../enums/submissions-sort-field.enum';
import { TimeTrackingHelperService } from 'src/app/time-tracking/services/time-tracking-helper/time-tracking-helper.service';
import { SubmissionActionType } from '../../enums/submission-action-type.enum';
import { RequestVerificationStatus } from 'src/app/requests/enums/verification-status.enum';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NavigationItemId } from 'src/app/modals/edl-modal-details/navigation-item-id.enum';
import { UserRequestDetailsModal } from 'src/app/requests/modals/sm-user-request-details-modal.component';

@Component({
    selector: 'edl-submissions-list',
    templateUrl: './submissions-list.component.html',
    styleUrls: ['./submissions-list.component.scss']
})
export class SubmissionsListComponent implements OnInit {
    private _requestItem: CplRequestItem;
    private _canManageItem: boolean = false;

    @Input() 
    get requestItem() { 
        return this._requestItem;
    }

    set requestItem(value: CplRequestItem) {
        this._requestItem = value;
        this._canManageItem = this.requestHelpersService.canManageItem(this.requestItem, this.sessionService.user);
    };

    @Input() userRequests: CplUserRequest[] = [];
    @Input() isLoading: boolean = false;
    @Input() isMyItems: boolean;
    @Input() activeActionColumns: SubmissionActionType[] = [];

    get actionType(): UserRequestActionsTypes {
        return this.isMyItems 
            ? UserRequestActionsTypes.MY_ITEMS
            : UserRequestActionsTypes.ALL;
    }

    get CanManageItem() {
        return this._canManageItem;
    }

    /**
     * The "View Details" / evidence affordance requires manage rights AND view permission on the item.
     * CanManageItem is role-based (true for an admin/supervisor), but under Private Limited Access the
     * backend withholds CAN_VIEW_ITEM, so canViewItem is false and the evidence button must be hidden
     * (FR-008/FR-009). Uses the fully-loaded item when available so Permissions are present.
     */
    get CanViewEvidenceDetails(): boolean {
        return this.CanManageItem
            && this.requestHelpersService.canViewItem(this.completeRequestItem || this.requestItem);
    }

    completeRequestItem: CplRequestItem;
    portalSettings: PortalSettings;

    showFlexColumn: boolean = false;
    showAct48Column: boolean = false;

    RequestUserStatus = RequestUserStatus;
    TimeTrackingType = TimeTrackingTypes;
    CplRosterActionsTypes = CplRosterActionsTypes;
    NavigationItemId = NavigationItemId;

    UserRequestActionsTypes = UserRequestActionsTypes;
    SubmissionsSortField = SubmissionsSortField;

    constructor(
        private sessionService: SessionService,
        private requestItemService: RequestItemService,
        public a48HelpersService: A48HelpersService,
        public requestHelpersService: RequestHelpersService,
        private timeTrackingHelperService: TimeTrackingHelperService,
        public stateService: StateService,
        private ngbModal: NgbModal
    ) {
        this.portalSettings = this.sessionService.PortalSettings;
    }

    async ngOnInit() {
        await this.loadCompleteRequestItem();
        this.updateColumnVisibility();
    }

    private updateColumnVisibility() {
        this.showFlexColumn = !!this.requestItem?.FlexDetails?.Applicable;
        
        this.showAct48Column = this.portalSettings?.Act48Enabled && 
                              (this.requestItem?.Act48Detail.Applicable === true);
    }

    private async loadCompleteRequestItem() {
        this.isLoading = true;
        if (this.requestItem?.RequestID) {
            try {
                this.completeRequestItem = await firstValueFrom(
                    this.requestItemService.getRequestItem(this.requestItem.RequestID, true)
                ) as CplRequestItem;
            } catch (error) {
                console.error('Error fetching complete request item:', error);
                // Fallback to the basic request item
                this.completeRequestItem = this.requestItem;
            }
        } else {
            this.completeRequestItem = this.requestItem;
        }
        this.isLoading = false;
    }

    isFlexItem(): boolean {
        return this.showFlexColumn;
    }

    isAct48Enabled(): boolean {
        return this.showAct48Column;
    }

    public getFlexHours(userRequests: CplUserRequest) {
        return this.timeTrackingHelperService.getUserRequestDisplayHours(userRequests, this.requestItem);
    }

    refreshList = () => {
        this.sessionService.Loading(true);
        this.stateService.reload();
    }

    shouldShowColumn(actionType: SubmissionActionType): boolean {
        return this.activeActionColumns.includes(actionType);
    }

    canShowApproveButton(userRequest: CplUserRequest): boolean {
        return this.requestHelpersService.isRequestItemActive(this.requestItem) &&
               this.requestHelpersService.canPerformVerificationAction(userRequest, this.requestItem, RequestVerificationStatus.APPROVED);
    }

    async openAct48Modal(userRequest: CplUserRequest) {
        this.sessionService.Loading(true);

        try {
            const shouldRefresh = await this.openDetailsModal(userRequest, NavigationItemId.ACT48);
            if (shouldRefresh) {
                this.refreshList();
            }
        } catch (result) {
            // Modal dismissed by user or submission completed
            this.refreshList();
        } finally {
            this.sessionService.Loading(false);
        }
    }

    private async openDetailsModal(userRequest: CplUserRequest, navigationItemId: NavigationItemId) {
        let modalRef = this.ngbModal.open(UserRequestDetailsModal, {
            size: 'lg',
            backdrop: 'static',
            windowClass: 'edl-modal-details'
        });

        modalRef.componentInstance.userRequest = userRequest;
        modalRef.componentInstance.request = this.completeRequestItem || this.requestItem;
        modalRef.componentInstance.navigationItemId = navigationItemId;

        return modalRef.result;
    }

    SubmissionActionType = SubmissionActionType;

    /**
     * EDLP-3580: params for the "View" link into the shared request-details page. Cancel on that
     * page always requires a reason, regardless of whether this list is My Items or All Users
     * Progress -- unlike the gearbox, which always opens the optional-notes modal instead.
     */
    getViewParams(userRequest: CplUserRequest) {
        return {
            requestId: this.requestItem.RequestID,
            userRequestId: userRequest.UserRequestID
        };
    }
}